import * as Toast from '../util/Toast'
import * as apilib from 'lib/api/index'

import {
  ActivityIndicator,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native'
import {RichText} from '@usedispatch/atproto-api'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useStores} from 'state/index'
// TODO: Prevent naming components that coincide with RN primitives
// due to linting false positives
import {TextInput, TextInputRef} from './text-input/TextInput'
import {colors, gradients, s} from 'lib/styles'
import {
  getImageUri,
  getMallowMintAddress,
  getPostUrl,
  toShortUrl,
} from 'lib/strings/url-helpers'
import {
  isAndroid,
  isIOS,
  isMobileWeb,
  isNative,
  isWeb,
} from 'platform/detection'

import {CharProgress} from './char-progress/CharProgress'
import {ComposerOpts} from 'state/models/ui/shell'
// import {CreateNFTBtn} from './photos/CreateNFTBtn'
import {EmojiPickerButton} from './text-input/web/EmojiPicker.web'
import {ExternalEmbed} from './ExternalEmbed'
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome'
import {Gallery} from './photos/Gallery'
import {GalleryModel} from 'state/models/media/gallery'
import {LabelsBtn} from './labels/LabelsBtn'
import LinearGradient from 'react-native-linear-gradient'
import {MAX_GRAPHEME_LENGTH, SOLARPLEX_UI_URL} from 'lib/constants'
import {NFTGallery} from './photos/NFTGallery'
import {NFTGalleryModel} from 'state/models/media/nft-gallery'
import {OpenCameraBtn} from './photos/OpenCameraBtn'
import QuoteEmbed from '../util/post-embeds/QuoteEmbed'
// import {SelectLangBtn} from './select-language/SelectLangBtn'
// import {SelectPhotoBtn} from './photos/SelectPhotoBtn'
import {Text} from '../util/text/Text'
import {UserAutocompleteModel} from 'state/models/discovery/user-autocomplete'
import {UserAvatar} from '../util/UserAvatar'
import {cleanError} from 'lib/strings/errors'
import {insertMentionAt} from 'lib/strings/mention-manip'
import {observer} from 'mobx-react-lite'
import {sanitizeDisplayName} from 'lib/strings/display-names'
import {sanitizeHandle} from 'lib/strings/handles'
import {shortenLinks} from 'lib/strings/rich-text-manip'
import {useAnalytics} from 'lib/analytics/analytics'
import {useExternalLinkFetch} from './useExternalLinkFetch'
import {useIsKeyboardVisible} from 'lib/hooks/useIsKeyboardVisible'
import {usePalette} from 'lib/hooks/usePalette'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {useWebMediaQueries} from 'lib/hooks/useWebMediaQueries'
// import {SelectVideoBtn} from './photos/SelectVideoBtn'
// import {CreateVideoNftBtn} from './photos/CreateVideoNftBtn'
import {ChannelDropDown} from '../util/forms/ChannelDropdown'
import {ImgVidDropdown} from '../util/forms/ImgVidDropdown'
import {NftDropDown} from '../util/forms/NftDropDown'

type Props = ComposerOpts
export const ComposePost = observer(function ComposePost({
  replyTo,
  onPost,
  quote: initQuote,
  mention: initMention,
}: Props) {
  const {track} = useAnalytics()
  const pal = usePalette('default')
  const {isDesktop, isMobile} = useWebMediaQueries()
  const store = useStores()
  const isThisSharing = store.shell.isSharing
  const sharingText = store.shell.sharingText
  const uri = store.shell.sharedUri
  const textInput = useRef<TextInputRef>(null)
  const [isKeyboardVisible] = useIsKeyboardVisible({iosUseWillEvents: true})
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingState, setProcessingState] = useState('')
  const [error, setError] = useState('')
  const [richtext, setRichText] = useState(
    new RichText({
      text: initMention
        ? insertMentionAt(
            `@${initMention}`,
            initMention.length + 1,
            `${initMention}`,
          ) // insert mention if passed in
        : sharingText
        ? sharingText
        : '',
    }),
  )
  const graphemeLength = useMemo(() => {
    return shortenLinks(richtext).graphemeLength
  }, [richtext])
  const [quote, setQuote] = useState<ComposerOpts['quote'] | undefined>(
    initQuote,
  )
  const {extLink, setExtLink} = useExternalLinkFetch({setQuote})
  const [labels, setLabels] = useState<string[]>([])
  const [hashtag, setHashtag] = useState<string>('')
  const [suggestedLinks, setSuggestedLinks] = useState<Set<string>>(new Set())
  const gallery = useMemo(() => new GalleryModel(store), [store])
  const nftGallery = useMemo(() => new NFTGalleryModel(store), [store])
  const isCreatorVerified = store.creatorTools.isVerified
  const onClose = useCallback(() => {
    store.shell.closeComposer()
  }, [store])

  const autocompleteView = useMemo<UserAutocompleteModel>(
    () => new UserAutocompleteModel(store),
    [store],
  )

  const insets = useSafeAreaInsets()
  const viewStyles = useMemo(
    () => ({
      paddingBottom:
        isAndroid || (isIOS && !isKeyboardVisible) ? insets.bottom : 0,
      paddingTop: isAndroid ? insets.top : isMobile ? 15 : 0,
    }),
    [insets, isKeyboardVisible, isMobile],
  )

  // TODO(viksit): this should be set only when the nft is set from the modal
  // once the nft meta modal is invoked and data entered,
  // we should use a setNftData to set this.
  // otherwise this should be empty by default.
  // this way the image gallery works as expected for the regular code flow.

  const onPressCancel = useCallback(() => {
    if (graphemeLength > 0 || !gallery.isEmpty || !nftGallery.isEmpty) {
      if (store.shell.activeModals.some(modal => modal.name === 'confirm')) {
        store.shell.closeModal()
      }
      if (Keyboard) {
        Keyboard.dismiss()
      }
      store.shell.openModal({
        name: 'confirm',
        title: 'Discard draft',
        onPressConfirm: onClose,
        onPressCancel: () => {
          store.shell.closeModal()
        },
        message: "Are you sure you'd like to discard this draft?",
        confirmBtnText: 'Discard',
        confirmBtnStyle: {backgroundColor: colors.red4},
      })
    } else {
      onClose()
    }
  }, [store, onClose, graphemeLength, gallery, nftGallery])

  // Note: we don't want to support paste into nft gallery for now.
  // initial setup
  useEffect(() => {
    autocompleteView.setup()
    if (isThisSharing && uri) {
      gallery.paste(uri)
    }
  }, [autocompleteView, gallery, isThisSharing, uri])

  useEffect(() => {
    if (suggestedLinks.size > 0) {
      onPressAddLinkCard(Array.from(suggestedLinks)[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedLinks])

  // listen to escape key on desktop web
  const onEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onPressCancel()
      }
    },
    [onPressCancel],
  )
  useEffect(() => {
    if (isWeb) {
      window.addEventListener('keydown', onEscape)
      return () => window.removeEventListener('keydown', onEscape)
    }
  }, [onEscape])

  const onPressAddLinkCard = useCallback(
    (uri: string) => {
      setExtLink({uri, isLoading: true})
    },
    [setExtLink],
  )

  const onPhotoPasted = useCallback(
    async (uri: string) => {
      track('Composer:PastedPhotos')
      await gallery.paste(uri)
    },
    [gallery, track],
  )

  const onPressPublish = async () => {
    if (isProcessing || graphemeLength > MAX_GRAPHEME_LENGTH) {
      return
    }
    if (store.preferences.requireAltTextEnabled && gallery.needsAltText) {
      return
    }

    setError('')

    if (richtext.text.trim().length === 0 && gallery.isEmpty) {
      setError('Did you want to say anything?')
      return
    }

    setIsProcessing(true)
    try {
      let nftEmbed
      if (
        !nftGallery.isEmpty &&
        nftGallery.name &&
        nftGallery.description &&
        nftGallery.mintId
      ) {
        // we should only allow one image for now
        // todo(viksit): error out if more
        // const nftImage = nftGallery.images[0]
        // const nftImageBlob = nftGallery.imageBlobs[0]

        nftEmbed = {
          collCid: nftGallery.mintId,
          collUri: nftGallery.uri,
        }
      }
      const res = await apilib.post(store, {
        rawText: richtext.text,
        replyTo: replyTo?.uri,
        images: gallery.images,
        videos: gallery.videos,
        quote,
        extLink,
        labels,
        nft: nftEmbed,
        onStateChange: setProcessingState,
        knownHandles: autocompleteView.knownHandles,
        langs: store.preferences.postLanguages,
      })

      if (res && nftEmbed && nftGallery.imageCid) {
        const uiPostUri = getPostUrl(res.uri, store.me.handle)
        const imageUri = getImageUri(store.me.did, nftGallery.imageCid)

        const twitterIntentLink = encodeURI(
          `https://twitter.com/intent/tweet?url=${SOLARPLEX_UI_URL}${uiPostUri}&text=I just created ${nftGallery.name} on @solarplex_xyz!`,
        )
        store.shell.openModal({
          name: 'collectible-created-success',
          imageSrc: imageUri,
          twitterIntentLink: twitterIntentLink,
        })
      }
    } catch (e: any) {
      if (extLink) {
        setExtLink({
          ...extLink,
          isLoading: true,
          localThumb: undefined,
        } as apilib.ExternalEmbedDraft)
      }
      setError(cleanError(e.message))
      setIsProcessing(false)
      return
    } finally {
      // add event for nft creation if nft gallery is not empty
      track('Create Post', {
        imageCount: gallery.size,
      })
      if (replyTo && replyTo.uri) track('Post:Reply')
    }
    if (!replyTo) {
      store.me.mainFeed.onPostCreated()
    }
    store.preferences.savePostLanguageToHistory()
    onPost?.()
    onClose()
    Toast.show(`Your ${replyTo ? 'reply' : 'post'} has been published`)
  }

  const canPost = useMemo(
    () =>
      graphemeLength <= MAX_GRAPHEME_LENGTH &&
      (!store.preferences.requireAltTextEnabled || !gallery.needsAltText) &&
      (nftGallery.isEmpty || !nftGallery.needsMetadata),
    [
      graphemeLength,
      store.preferences.requireAltTextEnabled,
      gallery.needsAltText,
      nftGallery.needsMetadata,
      nftGallery.isEmpty,
    ],
  )
  const selectTextInputPlaceholder = replyTo ? 'Write your reply' : `What's up?`

  const canSelectImages = useMemo(() => gallery.size < 4, [gallery.size])
  const hasMedia = gallery.size > 0 || Boolean(extLink)

  const allowSetLables = false

  const addChannel = useCallback((channelTag: string) => {
    setHashtag(channelTag)
  }, [])

  return (
    <KeyboardAvoidingView
      testID="composePostView"
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.outer}>
      <View style={[s.flex1, viewStyles]} aria-modal accessibilityViewIsModal>
        <View style={[styles.topbar, isDesktop && styles.topbarDesktop]}>
          <TouchableOpacity
            testID="composerDiscardButton"
            onPress={onPressCancel}
            onAccessibilityEscape={onPressCancel}
            accessibilityRole="button"
            accessibilityLabel="Cancel"
            accessibilityHint="Closes post composer and discards post draft">
            <Text style={[pal.link, s.f18]}>Cancel</Text>
          </TouchableOpacity>
          <View style={s.flex1} />
          {isProcessing ? (
            <>
              <Text style={pal.textLight}>{processingState}</Text>
              <View style={styles.postBtn}>
                <ActivityIndicator />
              </View>
            </>
          ) : (
            <>
              {isMobileWeb && (
                <View style={{paddingRight: 4}}>
                  <ChannelDropDown
                    setChannel={addChannel}
                    currentHashtag={hashtag}
                  />
                </View>
              )}
              {allowSetLables && (
                <LabelsBtn
                  labels={labels}
                  onChange={setLabels}
                  hasMedia={hasMedia}
                />
              )}

              {canPost ? (
                <TouchableOpacity
                  testID="composerPublishBtn"
                  onPress={onPressPublish}
                  accessibilityRole="button"
                  accessibilityLabel={
                    replyTo ? 'Publish reply' : 'Publish post'
                  }
                  accessibilityHint="">
                  <LinearGradient
                    colors={[gradients.purple.start, gradients.purple.end]}
                    start={{x: 0, y: 0}}
                    end={{x: 1, y: 1}}
                    style={styles.postBtn}>
                    <Text style={[s.white, s.f16, s.bold]}>
                      {replyTo ? 'Reply' : 'Post'}
                    </Text>
                  </LinearGradient>
                </TouchableOpacity>
              ) : (
                <>
                  <View style={[styles.postBtn, pal.btn]}>
                    <Text style={[pal.textLight, s.f16, s.bold]}>Post</Text>
                  </View>
                </>
              )}
            </>
          )}
        </View>
        {store.preferences.requireAltTextEnabled && gallery.needsAltText && (
          <View style={[styles.reminderLine, pal.viewLight]}>
            <View style={styles.errorIcon}>
              <FontAwesomeIcon
                icon="exclamation"
                style={{color: colors.red4}}
                size={10}
              />
            </View>
            <Text style={[pal.text, s.flex1]}>
              One or more images is missing alt text.
            </Text>
          </View>
        )}
        {error !== '' && (
          <View style={styles.errorLine}>
            <View style={styles.errorIcon}>
              <FontAwesomeIcon
                icon="exclamation"
                style={{color: colors.red4}}
                size={10}
              />
            </View>
            <Text style={[s.red4, s.flex1]}>{error}</Text>
          </View>
        )}
        <ScrollView
          style={styles.scrollView}
          keyboardShouldPersistTaps="always">
          {replyTo ? (
            <View style={[pal.border, styles.replyToLayout]}>
              <UserAvatar avatar={replyTo.author.avatar} size={50} />
              <View style={styles.replyToPost}>
                <Text type="xl-medium" style={[pal.text]}>
                  {sanitizeDisplayName(
                    replyTo.author.displayName ||
                      sanitizeHandle(replyTo.author.handle),
                  )}
                </Text>
                <Text type="post-text" style={pal.text} numberOfLines={6}>
                  {replyTo.text}
                </Text>
              </View>
            </View>
          ) : undefined}

          <View
            style={[
              pal.border,
              styles.textInputLayout,
              isNative && styles.textInputLayoutMobile,
            ]}>
            <UserAvatar avatar={store.me.avatar} size={50} />
            <TextInput
              ref={textInput}
              richtext={richtext}
              placeholder={selectTextInputPlaceholder}
              suggestedLinks={suggestedLinks}
              autocompleteView={autocompleteView}
              autoFocus={true}
              setRichText={setRichText}
              onPhotoPasted={onPhotoPasted}
              onPressPublish={onPressPublish}
              onSuggestedLinksChanged={setSuggestedLinks}
              onError={setError}
              accessible={true}
              hashtag={hashtag}
              accessibilityLabel="Write post"
              accessibilityHint={`Compose posts up to ${MAX_GRAPHEME_LENGTH} characters in length`}
            />
          </View>

          <Gallery gallery={gallery} />
          <NFTGallery addChannel={addChannel} gallery={nftGallery} />

          {gallery.isEmpty && extLink && (
            <ExternalEmbed
              link={extLink}
              onRemove={() => setExtLink(undefined)}
            />
          )}
          {quote ? (
            <View style={[s.mt5, isWeb && s.mb10]}>
              <QuoteEmbed quote={quote} />
            </View>
          ) : undefined}
        </ScrollView>
        {!extLink && suggestedLinks.size > 0 ? (
          <View style={s.mb5}>
            {Array.from(suggestedLinks)
              .slice(0, 3)
              .map(url => (
                <TouchableOpacity
                  key={`suggested-${url}`}
                  testID="addLinkCardBtn"
                  style={[pal.borderDark, styles.addExtLinkBtn]}
                  onPress={() => {
                    getMallowMintAddress(url) &&
                      track('NFT:MallowEmbedAdd', {
                        mallowLink: url,
                        mintAddress: getMallowMintAddress(url),
                        poster: store.session.currentSession?.handle,
                      })
                    onPressAddLinkCard(url)
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Add link card"
                  accessibilityHint={`Creates a card with a thumbnail. The card links to ${url}`}>
                  <Text style={pal.text}>
                    {getMallowMintAddress(url)
                      ? "Add 'Buy From Mallow' card:"
                      : ' Add link card:'}{' '}
                    <Text style={pal.link}>{toShortUrl(url)}</Text>
                  </Text>
                </TouchableOpacity>
              ))}
          </View>
        ) : null}
        <View style={[pal.border, styles.bottomBar]}>
          {canSelectImages ? (
            <>
              {/* <SelectPhotoBtn gallery={gallery} disable={!nftGallery.isEmpty} />
              <SelectVideoBtn gallery={gallery} disable={!nftGallery.isEmpty} /> */}
              <ImgVidDropdown gallery={gallery} disable={!nftGallery.isEmpty} />
              <OpenCameraBtn gallery={gallery} />
              {isCreatorVerified ? (
                <View style={{paddingLeft: 6}}>
                  {/* <CreateNFTBtn
                    gallery={nftGallery}
                    disable={!gallery.isEmpty}
                  />
                  <CreateVideoNftBtn
                    gallery={nftGallery}
                    disable={!gallery.isEmpty}
                  /> */}
                  <NftDropDown
                    gallery={nftGallery}
                    disable={!gallery.isEmpty}
                    addChannel={addChannel}
                  />
                </View>
              ) : undefined}
            </>
          ) : null}

          {isDesktop ? <EmojiPickerButton /> : null}
          <View style={s.flex1} />

          {/* <SelectLangBtn /> */}

          <CharProgress count={graphemeLength} />
          {!isMobileWeb && (
            <View style={{paddingHorizontal: 10}}>
              <ChannelDropDown
                setChannel={addChannel}
                currentHashtag={hashtag}
              />
            </View>
          )}
        </View>
      </View>
    </KeyboardAvoidingView>
  )
})

const styles = StyleSheet.create({
  outer: {
    flexDirection: 'column',
    flex: 1,
    height: '100%',
  },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 4,
    paddingHorizontal: 20,
    height: 55,
  },
  topbarDesktop: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  postBtn: {
    borderRadius: 20,
    paddingHorizontal: 20,
    paddingVertical: 6,
  },
  errorLine: {
    flexDirection: 'row',
    backgroundColor: colors.red1,
    borderRadius: 6,
    marginHorizontal: 15,
    paddingHorizontal: 8,
    paddingVertical: 6,
    marginVertical: 6,
  },
  reminderLine: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 6,
    marginHorizontal: 15,
    paddingHorizontal: 8,
    paddingVertical: 6,
    marginBottom: 6,
  },
  errorIcon: {
    borderWidth: 1,
    borderColor: colors.red4,
    color: colors.red4,
    borderRadius: 30,
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 5,
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: 15,
  },
  textInputLayout: {
    flexDirection: 'row',
    borderTopWidth: 1,
    paddingTop: 16,
  },
  textInputLayoutMobile: {
    flex: 1,
  },
  replyToLayout: {
    flexDirection: 'row',
    borderTopWidth: 1,
    paddingTop: 16,
    paddingBottom: 16,
  },
  replyToPost: {
    flex: 1,
    paddingLeft: 13,
    paddingRight: 8,
  },
  addExtLinkBtn: {
    borderWidth: 1,
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 10,
    marginBottom: 4,
  },
  bottomBar: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingLeft: 15,
    paddingRight: 20,
    alignItems: 'center',
    borderTopWidth: 1,
  },
})
